import { defineAbility } from 'nuxt-authorization/utils';

import type { AuthUser } from '~~/shared/types';
import { AbilitiesPermissions } from '~~/shared/types/abilities';
import type { PermissionKey, ResourceScopes } from '~~/shared/types/abilities';

export const makeMemberAbility = (
  resource: string,
  required: AbilitiesPermissions,
  filter: boolean = false
) =>
  defineAbility((auth: AuthUser) => {
    const { user, organizationPreferences, member } = auth;

    // Admin bypass
    if (user.role === 'admin') {
      return filter ? false : true;
    }

    // Role-based permissions
    const roleMask =
      organizationPreferences.roles?.[member.role!]?.[resource as PermissionKey] ??
      AbilitiesPermissions.None;

    if (hasPermission(roleMask, required)) {
      return true;
    }

    return false;
  });

export const makeScopedMemberAbility = (
  scopes: readonly ResourceScopes[],
  required: AbilitiesPermissions
) =>
  defineAbility((auth: AuthUser) => {
    if (auth.user.role === 'admin') {
      return true;
    }
    const permissions = auth.organizationPreferences.roles?.[auth.member.role!];
    return scopes.some((scope) => hasPermission(permissions?.[scope] ?? 0, required));
  });

export const makeOwnerAbility = () => defineAbility((auth: AuthUser) => auth.user.role === 'admin');

export const hasPermission = (mask: number, required: AbilitiesPermissions): boolean =>
  (mask & required) !== 0;

export const addPermission = (mask: number, perm: AbilitiesPermissions): number => mask | perm;

export const removePermission = (mask: number, perm: AbilitiesPermissions): number => mask & ~perm;
